import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  ArrowLeftIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../hooks/useAuth';
import { CommunicationLanguage, isAdminUser } from '../types/auth';
import Modal from '../components/common/Modal';
import {
  MembershipType,
  Study,
  StudyDegreeType,
  ContactPreference,
  ProfessionalPath,
  WorkSector,
} from '../types/member';
import { authService } from '../services/auth';
import { adminService } from '../services/admin';
import { applicationStatusService } from '../services/applicationStatus';
import { committeeService } from '../services/committees';
import { InternationalCommittee } from '../types/committee';
import FormField from '../components/common/FormField';
import PasswordVisibilityToggle from '../components/common/PasswordVisibilityToggle';
import FileUpload from '../components/common/FileUpload';
import StudiesSection from '../components/registration/StudiesSection';
import { normalizeCommitteeCodes, toCommitteeCode, uniqueInternationalCommittees } from '../utils/committeeUtils';
import { validateStudiesList } from '../utils/studyValidation';
import PersonalInfoSection from '../components/registration/PersonalInfoSection';
import ProfessionalActivitiesSection from '../components/registration/ProfessionalActivitiesSection';
import WorkDetailsSection from '../components/registration/WorkDetailsSection';
import MembershipSection from '../components/registration/MembershipSection';
import AuthWizardShell from '../components/registration/AuthWizardShell';
import WizardStepPanel from '../components/registration/WizardStepPanel';
import { Stepper } from '../design-system/components/Stepper';
import { Button } from '../design-system/components/Button';
import { Card } from '../design-system/components/Card';
import { Text } from '../design-system/components/Typography';
import { FormSection } from '../design-system/patterns/FormSection';
import {
  Form,
  FormRow,
  CheckboxContainer,
  Checkbox,
  CheckboxLabel,
} from '../design-system/patterns/FormLayout';
import { spacing } from '../design-system/tokens/spacing';
import { borders } from '../design-system/tokens/borders';
import { typography } from '../design-system/tokens/typography';
import { breakpoints } from '../design-system/tokens/breakpoints';
import { animations } from '../design-system/tokens/animations';
import { hexToRgba } from '../utils/colorUtils';
import * as validation from '../utils/validation';
import { getApiErrorMessage, getValidationErrorMessage } from '../utils/apiErrors';
import { focusFormError } from '../utils/focusFormError';
import {
  COMMITTEES_INFO_LIST,
  createEmptyMemberForm,
  getEffectiveProfessionalPath,
  memberDtoToFormData,
} from '../utils/memberForm';
import { GREECE } from '../data/countries';


const WizardCard = styled(Card)`
  padding: ${spacing[6]};
  max-width: 960px;
  margin: 0 auto;
  width: 100%;

  ${breakpoints.down.md} {
    padding: ${spacing[4]};
    box-shadow: none;
    border: none;
    background: transparent;
  }
`;

const RegisterLayout = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${spacing[6]};
  align-items: flex-start;
  width: 100%;

  @media (max-width: ${breakpoints.md}) {
    flex-direction: column;
    gap: ${spacing[4]};
  }
`;

const StepperColumn = styled.aside`
  flex-shrink: 0;
  width: 200px;
  position: sticky;
  top: ${spacing[2]};

  @media (max-width: ${breakpoints.md}) {
    display: none;
  }
`;

const StepperWrapper = styled.div`
  padding: ${spacing[2]} 0;
`;

const FormColumn = styled.div`
  flex: 1;
  min-width: 0;
`;

const MobileProgress = styled.div`
  display: none;
  margin-bottom: ${spacing[4]};

  @media (max-width: ${breakpoints.md}) {
    display: block;
  }
`;

const ProgressMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: ${spacing[3]};
  margin-bottom: ${spacing[2]};
`;

const ProgressTrack = styled.div`
  height: 4px;
  border-radius: ${borders.radius.full};
  background: ${props => props.theme.colors.borderLight};
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $percent: number }>`
  height: 100%;
  width: ${props => props.$percent}%;
  background: ${props => props.theme.colors.gradientPrimary};
  transition: width ${animations.duration.normal} ${animations.easing.easeOut};
`;

const AlertBanner = styled.div<{ $variant: 'error' | 'info' }>`
  display: flex;
  align-items: flex-start;
  gap: ${spacing[3]};
  padding: ${spacing[3]} ${spacing[4]};
  border-radius: ${borders.radius.lg};
  margin-bottom: ${spacing[5]};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
  line-height: ${typography.lineHeight.relaxed};
  border-left: 3px solid ${props =>
    props.$variant === 'error' ? props.theme.colors.error : props.theme.colors.primary};
  background: ${props =>
    props.$variant === 'error'
      ? hexToRgba(props.theme.colors.error, 0.08)
      : props.theme.colors.icomLightBlue || hexToRgba(props.theme.colors.primary, 0.08)};
  color: ${props =>
    props.$variant === 'error' ? props.theme.colors.error : props.theme.colors.textSecondary};

  svg {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    margin-top: 1px;
  }
`;

const ButtonGroupRight = styled.div`
  margin-left: auto;
  display: flex;
  gap: ${spacing[3]};
`;

const BackLink = styled(Link)`
  color: ${props => props.theme.colors.primary};
  text-decoration: none;
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  font-family: ${typography.fontFamily.primary};
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  margin-bottom: ${spacing[4]};
  transition: color 0.2s ease;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    color: ${props => props.theme.colors.primaryDark};
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 2px;
    border-radius: ${borders.radius.sm};
  }
`;

const SectionHeadingWithTop = styled.h3`
  color: ${props => props.theme.colors.text};
  margin-bottom: ${spacing[4]};
  margin-top: ${spacing[8]};
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.xl};
  font-weight: 600;
`;

const StatementBlock = styled.div`
  padding: ${spacing[4]};
  background: ${props => props.theme.colors.icomWarmGray || props.theme.colors.surfaceHover};
  border-radius: ${borders.radius.lg};
  border: ${borders.width.thin} solid ${props => props.theme.colors.border};
  margin-bottom: ${spacing[4]};
  font-size: ${typography.fontSize.sm};
  line-height: 1.7;
  color: ${props => props.theme.colors.text};
  white-space: pre-wrap;
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.primary};
  cursor: pointer;
  text-decoration: underline;
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
  padding: 0;

  &:hover {
    color: ${props => props.theme.colors.primaryDark};
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 2px;
    border-radius: ${borders.radius.sm};
  }
`;

const SpacedBlock = styled.div`
  margin-bottom: ${spacing[4]};
`;

const TextWithMargin = styled(Text)`
  margin-bottom: ${spacing[4]};
  display: block;
`;

const ExternalLink = styled.a`
  color: ${props => props.theme.colors.primary};
  margin-bottom: ${spacing[4]};
  display: block;
`;

const ScrollableStatementBlock = styled(StatementBlock)`
  max-height: 60vh;
  overflow-y: auto;
`;

const CommitteesList = styled.ul`
  margin: ${spacing[4]} 0 0 0;
  padding-left: ${spacing[6]};
  color: ${props => props.theme.colors.text};
  font-size: ${typography.fontSize.sm};
  line-height: 1.6;
`;

const GdprTabs = styled.div`
  display: flex;
  gap: ${spacing[2]};
  margin-bottom: ${spacing[4]};
`;

const GdprTab = styled.button<{ $active?: boolean }>`
  padding: ${spacing[2]} ${spacing[4]};
  border-radius: ${borders.radius.md};
  border: ${borders.width.thin} solid ${props =>
    props.$active ? props.theme.colors.primary : props.theme.colors.border};
  background: ${props => (props.$active ? props.theme.colors.primary : 'transparent')};
  color: ${props => (props.$active ? props.theme.colors.surface : props.theme.colors.text)};
  cursor: pointer;
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
  transition: ${animations.transition(
    ['background-color', 'border-color', 'color'],
    animations.duration.fast,
    animations.easing.easeOut
  )};

  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 2px;
  }
`;

/** Logical wizard steps (stable IDs; work/studies may be skipped in navigation). */
const INDIVIDUAL_STEP = {
  PERSONAL: 1,
  ACCOUNT: 2,
  CATEGORY: 3,
  WORK: 4,
  STUDIES: 5,
  COMMITTEES: 6,
  DOCUMENTS: 7,
} as const;

const CONSENT_ERROR_KEYS = ['acceptedEthics', 'acceptedGdpr'];
const FINAL_STEP_SUBMIT_GRACE_MS = 400;

const RegisterIndividual: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const { success: showSuccess, error: showError } = useToast();

  const mode = searchParams.get('mode');
  const referenceParam = searchParams.get('reference')?.trim() || '';
  const isResubmit = mode === 'resubmit';
  const isAdminCreate = mode === 'admin';
  const skipAccountStep = isResubmit;

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [modeLoading, setModeLoading] = useState(isResubmit || isAdminCreate);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [committees, setCommittees] = useState<InternationalCommittee[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>(
    {}
  );
  const [committeesModalOpen, setCommitteesModalOpen] = useState(false);
  const [committeesLoadError, setCommitteesLoadError] = useState<string | null>(null);
  const [committeesReady, setCommitteesReady] = useState(false);
  const [gdprModalOpen, setGdprModalOpen] = useState(false);
  const [gdprVariant, setGdprVariant] = useState<'with' | 'without'>('with');
  const [hasAttemptedFinalSubmit, setHasAttemptedFinalSubmit] = useState(false);
  const [applicantNote, setApplicantNote] = useState('');
  const [resubmitReference, setResubmitReference] = useState(referenceParam);
  const lastStepEnteredAtRef = useRef(0);
  const resubmitLoadedRef = useRef(false);

  // Get current language and map to CommunicationLanguage
  const getCurrentCommunicationLanguage = (): CommunicationLanguage => {
    const lang = i18n.language.toLowerCase();
    if (lang.startsWith('el') || lang.startsWith('gr')) return CommunicationLanguage.Greek;
    if (lang.startsWith('fr')) return CommunicationLanguage.French;
    if (lang.startsWith('es')) return CommunicationLanguage.Spanish;
    return CommunicationLanguage.English;
  };

  // Form data
  const [formData, setFormData] = useState(() =>
    createEmptyMemberForm(getCurrentCommunicationLanguage())
  );

  useEffect(() => {
    if (isAdminCreate && (!isAuthenticated || !isAdminUser(user))) {
      navigate('/login', { replace: true });
      return;
    }
    if (isAdminCreate) {
      setFormData(createEmptyMemberForm(getCurrentCommunicationLanguage()));
      setModeLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminCreate, isAuthenticated, user]);

  useEffect(() => {
    loadCommittees();
  }, []);

  useEffect(() => {
    if (!isResubmit || resubmitLoadedRef.current || !committeesReady) return;

    const loadEditable = async () => {
      if (!referenceParam) {
        showError(t('applicationStatus.enterReference'));
        navigate('/application-status', { replace: true });
        return;
      }
      setModeLoading(true);
      try {
        const editable = await applicationStatusService.getEditable(referenceParam);
        if (!editable.canEdit || editable.type !== 'Individual' || !editable.individual) {
          showError(t('applicationStatus.resubmitError'));
          navigate(`/application-status?reference=${encodeURIComponent(referenceParam)}`, {
            replace: true,
          });
          return;
        }
        setResubmitReference(editable.applicationReferenceNumber);
        setFormData(
          memberDtoToFormData(editable.individual, committees, getCurrentCommunicationLanguage())
        );
        resubmitLoadedRef.current = true;
      } catch (err) {
        showError(err instanceof Error ? err.message : t('applicationStatus.resubmitError'));
        navigate(`/application-status?reference=${encodeURIComponent(referenceParam)}`, {
          replace: true,
        });
      } finally {
        setModeLoading(false);
      }
    };

    loadEditable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResubmit, committeesReady, committees, referenceParam]);

  useEffect(() => {
    setFormData(prev => {
      if (
        prev.residentialCountryEn === GREECE.nameEn &&
        prev.residentialCountryGr === GREECE.nameGr
      ) {
        return prev;
      }
      return {
        ...prev,
        residentialCountryEn: GREECE.nameEn,
        residentialCountryGr: GREECE.nameGr,
      };
    });
  }, []);

  useEffect(() => {
    if (step === INDIVIDUAL_STEP.DOCUMENTS && !formData.declarationDate) {
      setFormData(prev => ({
        ...prev,
        declarationDate: new Date().toISOString().slice(0, 10),
      }));
    }
  }, [step, formData.declarationDate]);

  useEffect(() => {
    setFormData(prev => ({ ...prev, communicationLanguage: getCurrentCommunicationLanguage() }));
  }, [i18n.language]);

  const loadCommittees = async () => {
    setCommitteesLoadError(null);
    try {
      const data = await committeeService.getAllCommittees();
      setCommittees(uniqueInternationalCommittees(data.filter(c => c.isActive)));
    } catch (err) {
      console.error('Failed to load committees:', err);
      const message = err instanceof Error ? err.message : t('register.committees.loadError');
      setCommitteesLoadError(message);
    } finally {
      setCommitteesReady(true);
    }
  };

  const validateField = (fieldName: string, value: any): string | undefined => {
    switch (fieldName) {
      case 'email':
        return validation.validateEmail(value).error;
      case 'password':
        return validation.validatePassword(value, 6).error;
      case 'confirmPassword':
        return validation.validatePasswordConfirmation(formData.password, value).error;
      case 'phone':
        return validation.validatePhone(value, false).error;
      case 'mobile':
        return validation.validatePhone(value, false).error;
      case 'professionalEmail':
        if (value && value.trim() !== '') {
          return validation.validateEmail(value).error;
        }
        return undefined;
      case 'professionalPhone':
        return validation.validatePhone(value, false).error;
      case 'dateOfBirth':
        return validation.validateOptionalDateParts(
          {
            day: formData.dateOfBirthDay,
            month: formData.dateOfBirthMonth,
            year: formData.dateOfBirthYear,
          },
          value || formData.dateOfBirth || ''
        ).error;
      case 'residentialPostalCode':
      case 'professionalPostalCode':
        return validation.validatePostalCode(value, false).error;
      default:
        return undefined;
    }
  };

  /** Per-study field errors keyed as studies.{id}.{field}. Also sets studies summary when empty. */
  const validateStudies = (studies: Study[]): Record<string, string> =>
    validateStudiesList(studies, t, { requireAtLeastOne: true });

  const validateStep = (stepToValidate: number = step): boolean => {
    setError(null);
    const errors: Record<string, string> = {};
    let isValid = true;
    const { working: isWorking, studying: isStudying, retired: isRetired } =
      formData.professionalActivities;

    if (stepToValidate === INDIVIDUAL_STEP.PERSONAL) {
      const hasFirst = !!(formData.firstNameEn?.trim() || formData.firstNameGr?.trim());
      if (!hasFirst) {
        errors.firstName = t('register.firstNameRequired');
        isValid = false;
      }
      const lastNameErrorKey = validation.bilingualLastNameErrorKey(
        formData.lastNameEn,
        formData.lastNameGr
      );
      if (lastNameErrorKey) {
        errors.lastName = t(lastNameErrorKey);
        isValid = false;
      }
      const dateError = validateField('dateOfBirth', formData.dateOfBirth);
      if (dateError) {
        errors.dateOfBirth = getValidationErrorMessage(dateError, t);
        isValid = false;
      }
      const phoneError = validateField('phone', formData.phone);
      if (phoneError) {
        errors.phone = getValidationErrorMessage(phoneError, t);
        isValid = false;
      }
      const mobileError = validateField('mobile', formData.mobile);
      if (mobileError) {
        errors.mobile = getValidationErrorMessage(mobileError, t);
        isValid = false;
      }
    } else if (stepToValidate === INDIVIDUAL_STEP.ACCOUNT && !skipAccountStep) {
      const emailError = validateField('email', formData.email);
      if (emailError) {
        errors.email = getValidationErrorMessage(emailError, t);
        isValid = false;
      }
      const passwordError = validateField('password', formData.password);
      if (passwordError) {
        errors.password = getValidationErrorMessage(passwordError, t);
        isValid = false;
      }
      const confirmPasswordError = validateField('confirmPassword', formData.confirmPassword);
      if (confirmPasswordError) {
        errors.confirmPassword = getValidationErrorMessage(confirmPasswordError, t);
        isValid = false;
      }
    } else if (stepToValidate === INDIVIDUAL_STEP.CATEGORY) {
      const atLeastOne = isWorking || isStudying || isRetired;
      if (!atLeastOne) {
        setError(t('register.professionalSelectAtLeastOne'));
        isValid = false;
      }
    } else if (stepToValidate === INDIVIDUAL_STEP.WORK && isWorking) {
      const hasInstitution = !!(formData.professionalInstitutionNameEn?.trim() || formData.professionalInstitutionNameGr?.trim());
      if (!hasInstitution) {
        errors.professionalInstitution = t('register.professionalInstitutionRequired');
        isValid = false;
      }
      const profEmailError = validateField('professionalEmail', formData.professionalEmail);
      if (profEmailError) {
        errors.professionalEmail = getValidationErrorMessage(profEmailError, t);
        isValid = false;
      }
      const profPhoneError = validateField('professionalPhone', formData.professionalPhone);
      if (profPhoneError) {
        errors.professionalPhone = getValidationErrorMessage(profPhoneError, t);
        isValid = false;
      }
    } else if (stepToValidate === INDIVIDUAL_STEP.STUDIES && isStudying) {
      if (!formData.studies?.length) {
        errors.studies = t('register.studiesRequired');
        isValid = false;
      } else {
        const studyErrors = validateStudies(formData.studies);
        Object.assign(errors, studyErrors);
        if (Object.keys(studyErrors).length > 0) {
          isValid = false;
          if (!errors.studies) {
            setError(t('register.formErrorsBelow'));
          }
        }
      }
    } else if (stepToValidate === INDIVIDUAL_STEP.COMMITTEES) {
      // Leaving Committees → Documents: only committee rules (never GDPR/ethics here).
      if (!formData.mainCommittee) {
        errors.mainCommittee = t('register.committees.mainRequired');
        setError(t('register.committees.mainRequired'));
        isValid = false;
      }
      if (formData.additionalCommittees.length > 3) {
        setError(t('register.committees.maxReached'));
        isValid = false;
      }
    } else if (stepToValidate === INDIVIDUAL_STEP.DOCUMENTS) {
      // Ethics / GDPR only when explicitly finishing (see handleSubmit).
      // Do not set these errors merely by visiting this step.
    }

    setFieldErrors(errors);
    return isValid;
  };

  /** Validates all steps for final submit (step 7). Returns false if any check fails. */
  const validateAllForSubmit = (): boolean => {
    setError(null);
    const errors: Record<string, string> = {};
    let isValid = true;
    const { working, studying } = formData.professionalActivities;

    // Step 1: name
    const hasFirst = !!(formData.firstNameEn?.trim() || formData.firstNameGr?.trim());
    if (!hasFirst) {
      errors.firstName = t('register.firstNameRequired');
      isValid = false;
    }
    const lastNameErrorKey = validation.bilingualLastNameErrorKey(
      formData.lastNameEn,
      formData.lastNameGr
    );
    if (lastNameErrorKey) {
      errors.lastName = t(lastNameErrorKey);
      isValid = false;
    }

    // Step 2: account (skip password for resubmit; email optional when prefilled)
    if (!skipAccountStep) {
      const emailError = validateField('email', formData.email);
      if (emailError) {
        errors.email = getValidationErrorMessage(emailError, t);
        isValid = false;
      }
      const passwordError = validateField('password', formData.password);
      if (passwordError) {
        errors.password = getValidationErrorMessage(passwordError, t);
        isValid = false;
      }
      const confirmPasswordError = validateField('confirmPassword', formData.confirmPassword);
      if (confirmPasswordError) {
        errors.confirmPassword = getValidationErrorMessage(confirmPasswordError, t);
        isValid = false;
      }
    }

    // Step 3: professional activities
    const atLeastOne = working || studying || formData.professionalActivities.retired;
    if (!atLeastOne) {
      setError(t('register.professionalSelectAtLeastOne'));
      isValid = false;
    }

    // Step 4 (if working): institution
    if (working) {
      const hasInstitution = !!(formData.professionalInstitutionNameEn?.trim() || formData.professionalInstitutionNameGr?.trim());
      if (!hasInstitution) {
        errors.professionalInstitution = t('register.professionalInstitutionRequired');
        isValid = false;
      }
    }

    // Step 5 (if studying): studies
    if (studying) {
      const studyErrors = validateStudies(formData.studies || []);
      Object.assign(errors, studyErrors);
      if (Object.keys(studyErrors).length > 0) isValid = false;
    }

    // Work contact fields when working
    if (working) {
      const profEmailError = validateField('professionalEmail', formData.professionalEmail);
      if (profEmailError) {
        errors.professionalEmail = getValidationErrorMessage(profEmailError, t);
        isValid = false;
      }
      const profPhoneError = validateField('professionalPhone', formData.professionalPhone);
      if (profPhoneError) {
        errors.professionalPhone = getValidationErrorMessage(profPhoneError, t);
        isValid = false;
      }
    }

    // Step 6: committees
    if (!formData.mainCommittee) {
      setError(t('register.committees.mainRequired'));
      isValid = false;
    }
    if (formData.additionalCommittees.length > 3) {
      setError(t('register.committees.maxReached'));
      isValid = false;
    }

    // Step 7: declaration — field-level only (caller shows summary)
    if (!formData.acceptedEthics) {
      errors.acceptedEthics = t('register.ethicsRequired');
      isValid = false;
    }
    if (!formData.acceptedGdpr) {
      errors.acceptedGdpr = t('register.gdprRequired');
      isValid = false;
    }
    if (!isValid && (errors.acceptedEthics || errors.acceptedGdpr)) {
      setError(t('register.formErrorsBelow'));
    }

    setFieldErrors(errors);
    return isValid;
  };

  useEffect(() => {
    if (error || Object.keys(fieldErrors).length > 0) {
      // Defer so the error banner and aria-invalid attributes are in the DOM.
      requestAnimationFrame(() => focusFormError('register-form-errors'));
    }
  }, [error, fieldErrors]);

  useEffect(() => {
    const scrollRoot = document.getElementById('register-form-scroll');
    if (scrollRoot) {
      scrollRoot.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [step]);

  const handleFieldBlur = (fieldName: string) => {
    setTouchedFields(prev => ({ ...prev, [fieldName]: true }));
    const value = (formData as unknown as Record<string, unknown>)[fieldName];
    const rawError = validateField(fieldName, value);
    setFieldErrors(prev => {
      const next = { ...prev };
      if (rawError) {
        next[fieldName] = getValidationErrorMessage(rawError, t);
      } else {
        delete next[fieldName];
      }
      return next;
    });
  };

  const handleFieldChange = (fieldName: string, value: any) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    if (fieldErrors[fieldName]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
    if (touchedFields[fieldName]) {
      const rawError = validateField(fieldName, value);
      if (rawError) {
        setFieldErrors(prev => ({
          ...prev,
          [fieldName]: getValidationErrorMessage(rawError, t),
        }));
      }
    }
  };

  const { working, studying } = formData.professionalActivities;
  const showWorkStep = working;
  const showStudiesStep = studying;
  const getNextStep = (current: number): number => {
    if (current === INDIVIDUAL_STEP.PERSONAL) {
      return skipAccountStep ? INDIVIDUAL_STEP.CATEGORY : INDIVIDUAL_STEP.ACCOUNT;
    }
    if (current === INDIVIDUAL_STEP.ACCOUNT) return INDIVIDUAL_STEP.CATEGORY;
    if (current === INDIVIDUAL_STEP.CATEGORY) {
      return showWorkStep
        ? INDIVIDUAL_STEP.WORK
        : showStudiesStep
          ? INDIVIDUAL_STEP.STUDIES
          : INDIVIDUAL_STEP.COMMITTEES;
    }
    if (current === INDIVIDUAL_STEP.WORK) {
      return showStudiesStep ? INDIVIDUAL_STEP.STUDIES : INDIVIDUAL_STEP.COMMITTEES;
    }
    if (current === INDIVIDUAL_STEP.STUDIES) return INDIVIDUAL_STEP.COMMITTEES;
    if (current === INDIVIDUAL_STEP.COMMITTEES) return INDIVIDUAL_STEP.DOCUMENTS;
    return INDIVIDUAL_STEP.DOCUMENTS;
  };
  const getPreviousStep = (current: number): number => {
    if (current === INDIVIDUAL_STEP.ACCOUNT) return INDIVIDUAL_STEP.PERSONAL;
    if (current === INDIVIDUAL_STEP.CATEGORY) {
      return skipAccountStep ? INDIVIDUAL_STEP.PERSONAL : INDIVIDUAL_STEP.ACCOUNT;
    }
    if (current === INDIVIDUAL_STEP.WORK) return INDIVIDUAL_STEP.CATEGORY;
    if (current === INDIVIDUAL_STEP.STUDIES) {
      return showWorkStep ? INDIVIDUAL_STEP.WORK : INDIVIDUAL_STEP.CATEGORY;
    }
    if (current === INDIVIDUAL_STEP.COMMITTEES) {
      return showStudiesStep
        ? INDIVIDUAL_STEP.STUDIES
        : showWorkStep
          ? INDIVIDUAL_STEP.WORK
          : INDIVIDUAL_STEP.CATEGORY;
    }
    if (current === INDIVIDUAL_STEP.DOCUMENTS) return INDIVIDUAL_STEP.COMMITTEES;
    return INDIVIDUAL_STEP.PERSONAL;
  };

  const handleNext = () => {
    // Validate only the step we are leaving (Committees must never run GDPR rules).
    if (!validateStep(step)) return;
    setError(null);
    setHasAttemptedFinalSubmit(false);
    setFieldErrors({});
    setStep(prev => getNextStep(prev));
  };

  // Never show ethics/GDPR errors merely by arriving on the final step.
  useEffect(() => {
    if (step !== INDIVIDUAL_STEP.DOCUMENTS) return;
    lastStepEnteredAtRef.current = Date.now();
    setHasAttemptedFinalSubmit(false);
    setError(null);
    setFieldErrors(prev => {
      if (!CONSENT_ERROR_KEYS.some(k => prev[k])) return prev;
      const next = { ...prev };
      CONSENT_ERROR_KEYS.forEach(k => delete next[k]);
      return next;
    });
  }, [step]);

  // Re-translate field/summary errors when language changes.
  useEffect(() => {
    if (!error && Object.keys(fieldErrors).length === 0) return;
    if (step === INDIVIDUAL_STEP.DOCUMENTS) {
      if (!hasAttemptedFinalSubmit) return;
      // Do not re-run consent validation on language change until submit.
      setFieldErrors(prev => {
        const next = { ...prev };
        if (next.acceptedEthics) next.acceptedEthics = t('register.ethicsRequired');
        if (next.acceptedGdpr) next.acceptedGdpr = t('register.gdprRequired');
        return next;
      });
      if (error) setError(t('register.formErrorsBelow'));
      return;
    }
    // Retranslate existing errors only; do not re-run validation on language change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const handlePrevious = () => {
    setError(null);
    setFieldErrors({});
    setHasAttemptedFinalSubmit(false);
    setStep(prev => getPreviousStep(prev));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const submitter = (e.nativeEvent as SubmitEvent).submitter as HTMLElement | null;
    const isFinalSubmitClick =
      !!submitter && submitter.getAttribute('data-action') === 'register-complete';

    // Enter / incidental submit on earlier steps: advance only (committee step must not hit GDPR).
    if (step !== INDIVIDUAL_STEP.DOCUMENTS || !isFinalSubmitClick) {
      if (step !== INDIVIDUAL_STEP.DOCUMENTS) {
        handleNext();
      }
      return;
    }

    // Ignore a click that was aimed at Continue and landed on Complete after the step swap.
    if (Date.now() - lastStepEnteredAtRef.current < FINAL_STEP_SUBMIT_GRACE_MS) {
      return;
    }

    setHasAttemptedFinalSubmit(true);
    // Documents step: GDPR / ethics only on explicit "Complete registration" submit.
    const declarationErrors: Record<string, string> = {};
    if (!formData.acceptedEthics) {
      declarationErrors.acceptedEthics = t('register.ethicsRequired');
    }
    if (!formData.acceptedGdpr) {
      declarationErrors.acceptedGdpr = t('register.gdprRequired');
    }
    if (Object.keys(declarationErrors).length > 0) {
      setFieldErrors(prev => ({ ...prev, ...declarationErrors }));
      setError(t('register.formErrorsBelow'));
      requestAnimationFrame(() => focusFormError('register-form-errors'));
      return;
    }
    if (!validateAllForSubmit()) {
      if (!skipAccountStep) {
        const accountInvalid =
          !formData.email?.trim() ||
          !formData.password ||
          formData.password !== formData.confirmPassword ||
          formData.password.length < 6;
        if (accountInvalid) {
          setStep(INDIVIDUAL_STEP.ACCOUNT);
          setHasAttemptedFinalSubmit(false);
        }
      }
      requestAnimationFrame(() => focusFormError('register-form-errors'));
      return;
    }
    setLoading(true);
    setError(null);
    setFieldErrors({});
    try {
      const effectivePath = getEffectiveProfessionalPath(formData.professionalActivities);
      const mainCommitteeCode = toCommitteeCode(committees, formData.mainCommittee);
      const additionalCommitteeCodes = normalizeCommitteeCodes(committees, formData.additionalCommittees);

      if (isResubmit) {
        await applicationStatusService.resubmit(resubmitReference, {
          applicantNote: applicantNote || undefined,
          firstNameEn: formData.firstNameEn || undefined,
          firstNameGr: formData.firstNameGr || undefined,
          lastNameEn: formData.lastNameEn || undefined,
          lastNameGr: formData.lastNameGr || undefined,
          title: formData.title || undefined,
          gender: formData.gender || undefined,
          dateOfBirth: formData.dateOfBirth || undefined,
          phone: formData.phone || undefined,
          mobile: formData.mobile || undefined,
          mobilePhone: formData.mobile || undefined,
          website: formData.website || undefined,
          contactPreference: formData.contactPreference || undefined,
          residentialAddressEn: formData.residentialAddressEn || undefined,
          residentialAddressGr: formData.residentialAddressGr || undefined,
          residentialCityEn: formData.residentialCityEn || undefined,
          residentialCityGr: formData.residentialCityGr || undefined,
          residentialPostalCode: formData.residentialPostalCode || undefined,
          residentialCountryEn: formData.residentialCountryEn || undefined,
          residentialCountryGr: formData.residentialCountryGr || undefined,
          professionalPath: effectivePath || undefined,
          isGraduateStudent: !!formData.isGraduateStudent,
          isRetired: !!formData.isRetired,
          isVolunteer: false,
          isHonoraryMember: !!formData.isHonoraryMember,
          membershipType: formData.membershipType,
          organisationNameEn: formData.organisationNameEn || undefined,
          organisationNameGr: formData.organisationNameGr || undefined,
          professionalTitleEn: formData.professionalTitleEn || undefined,
          professionalTitleGr: formData.professionalTitleGr || undefined,
          professionalInstitutionNameEn: formData.professionalInstitutionNameEn || undefined,
          professionalInstitutionNameGr: formData.professionalInstitutionNameGr || undefined,
          professionalAddressEn: formData.professionalAddressEn || undefined,
          professionalAddressGr: formData.professionalAddressGr || undefined,
          professionalPostalCode: formData.professionalPostalCode || undefined,
          professionalCityEn: formData.professionalCityEn || undefined,
          professionalCityGr: formData.professionalCityGr || undefined,
          professionalEmail: formData.professionalEmail || undefined,
          professionalPhone: formData.professionalPhone || undefined,
          workSector: formData.workSector || undefined,
          studies:
            formData.professionalActivities.studying && formData.studies?.length
              ? JSON.stringify(formData.studies)
              : undefined,
          mainCommittee: mainCommitteeCode || undefined,
          additionalCommittees: JSON.stringify(additionalCommitteeCodes),
          transferFromIcom: formData.transferFromIcom || undefined,
          previousCardNumber: formData.previousCardNumber || undefined,
          cv: formData.cv,
          professionalActivityDocuments: formData.professionalActivityDocuments,
          studentId: formData.studentID,
        });
        showSuccess(t('applicationStatus.resubmitSuccess'));
        navigate(
          `/application-status?reference=${encodeURIComponent(resubmitReference)}`
        );
        return;
      }

      // Submit full application data (including optional documents)
      const payload = new FormData();

      // Account
      payload.append('email', formData.email);
      payload.append('password', formData.password);
      payload.append('communicationLanguage', String(formData.communicationLanguage));

      // Personal (nested for backend binding: PascalCase to match C# Name, Residential)
      if (formData.title) payload.append('title', formData.title);
      if (formData.firstNameEn) payload.append('Name.FirstNameEn', formData.firstNameEn);
      if (formData.firstNameGr) payload.append('Name.FirstNameGr', formData.firstNameGr);
      if (formData.lastNameEn) payload.append('Name.LastNameEn', formData.lastNameEn);
      if (formData.lastNameGr) payload.append('Name.LastNameGr', formData.lastNameGr);
      if (formData.gender) payload.append('gender', String(formData.gender));
      if (formData.dateOfBirth) payload.append('dateOfBirth', formData.dateOfBirth);
      if (formData.website) payload.append('website', formData.website);
      if (formData.contactPreference) payload.append('contactPreference', formData.contactPreference);
      if (effectivePath) payload.append('professionalPath', effectivePath);
      if (formData.organisationNameEn) payload.append('organisationNameEn', formData.organisationNameEn);
      if (formData.organisationNameGr) payload.append('organisationNameGr', formData.organisationNameGr);

      // Address/contact (nested: Residential.* for C# binding)
      if (formData.residentialAddressEn) payload.append('Residential.AddressEn', formData.residentialAddressEn);
      if (formData.residentialAddressGr) payload.append('Residential.AddressGr', formData.residentialAddressGr);
      if (formData.residentialPostalCode) {
        payload.append('Residential.PostalCodeEn', formData.residentialPostalCode);
        payload.append('Residential.PostalCodeGr', formData.residentialPostalCode);
      }
      if (formData.residentialCityEn) payload.append('Residential.CityEn', formData.residentialCityEn);
      if (formData.residentialCityGr) payload.append('Residential.CityGr', formData.residentialCityGr);
      if (formData.residentialCountryEn) payload.append('Residential.CountryEn', formData.residentialCountryEn);
      if (formData.residentialCountryGr) payload.append('Residential.CountryGr', formData.residentialCountryGr);
      if (formData.phone) payload.append('phone', formData.phone);
      if (formData.mobile) payload.append('mobile', formData.mobile);

      // Professional
      if (formData.professionalTitleEn) payload.append('professionalTitleEn', formData.professionalTitleEn);
      if (formData.professionalTitleGr) payload.append('professionalTitleGr', formData.professionalTitleGr);
      if (formData.workSector) payload.append('workSector', String(formData.workSector));
      // Only persist studies when the student branch is selected (deselected branch discards them).
      if (formData.professionalActivities.studying && formData.studies?.length) {
        payload.append('studies', JSON.stringify(formData.studies));
      }
      payload.append('isGraduateStudent', String(!!formData.isGraduateStudent));
      payload.append('isRetired', String(!!formData.isRetired));
      payload.append('isVolunteer', 'false');
      payload.append('isHonoraryMember', String(!!formData.isHonoraryMember));
      if (formData.professionalInstitutionNameEn) payload.append('professionalInstitutionNameEn', formData.professionalInstitutionNameEn);
      if (formData.professionalInstitutionNameGr) payload.append('professionalInstitutionNameGr', formData.professionalInstitutionNameGr);
      if (formData.professionalAddressEn) payload.append('professionalAddressEn', formData.professionalAddressEn);
      if (formData.professionalAddressGr) payload.append('professionalAddressGr', formData.professionalAddressGr);
      if (formData.professionalPostalCode) payload.append('professionalPostalCode', formData.professionalPostalCode);
      if (formData.professionalCityEn) payload.append('professionalCityEn', formData.professionalCityEn);
      if (formData.professionalCityGr) payload.append('professionalCityGr', formData.professionalCityGr);
      if (formData.professionalEmail) payload.append('professionalEmail', formData.professionalEmail);
      if (formData.professionalPhone) payload.append('professionalPhone', formData.professionalPhone);
      if (formData.transferFromIcom) payload.append('transferFromIcom', formData.transferFromIcom);
      if (formData.previousCardNumber) payload.append('previousCardNumber', formData.previousCardNumber);

      // Membership / committees
      payload.append('membershipType', String(formData.membershipType));
      if (mainCommitteeCode) payload.append('mainCommittee', mainCommitteeCode);
      payload.append('additionalCommittees', JSON.stringify(additionalCommitteeCodes));

      // Declaration
      payload.append('acceptedEthics', String(!!formData.acceptedEthics));
      payload.append('acceptedGdpr', String(!!formData.acceptedGdpr));
      if (formData.declarationDate) payload.append('declarationDate', formData.declarationDate);

      // Documents (optional)
      if (formData.cv) payload.append('cv', formData.cv);
      if (formData.professionalActivityDocuments) payload.append('professionalActivityDocuments', formData.professionalActivityDocuments);
      if (formData.studentID) payload.append('studentId', formData.studentID);

      if (isAdminCreate) {
        const created = await adminService.createMember(payload);
        showSuccess(t('register.registrationSuccessful'));
        navigate(`/admin/members/${created.id}`);
        return;
      }

      const result = await authService.registerComplete(payload);

      showSuccess(t('register.registrationSuccessful'));
      navigate('/register-success', {
        state: {
          email: formData.email,
          message: t('register.checkEmailConfirmation'),
          applicationReferenceNumber: result.applicationReferenceNumber,
        },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      const friendlyMessage = getApiErrorMessage(message, t, { registration: true });
      setError(friendlyMessage);
      showError(friendlyMessage);
    } finally {
      setLoading(false);
    }
  };

  const stepsConfig: Array<{ logical: number; label: string; show: boolean }> = [
    { logical: INDIVIDUAL_STEP.PERSONAL, label: t('register.individual.step1'), show: true },
    { logical: INDIVIDUAL_STEP.ACCOUNT, label: t('register.individual.step2'), show: !skipAccountStep },
    { logical: INDIVIDUAL_STEP.CATEGORY, label: t('register.categoryStep'), show: true },
    { logical: INDIVIDUAL_STEP.WORK, label: t('register.individual.workStep'), show: showWorkStep },
    { logical: INDIVIDUAL_STEP.STUDIES, label: t('register.individual.studiesStep'), show: showStudiesStep },
    { logical: INDIVIDUAL_STEP.COMMITTEES, label: t('register.individual.step4'), show: true },
    { logical: INDIVIDUAL_STEP.DOCUMENTS, label: t('register.individual.step5'), show: true },
  ];
  const visibleSteps = stepsConfig.filter(s => s.show);
  const stepperSteps = visibleSteps.map(s => {
    const state = step > s.logical ? 'completed' as const : step === s.logical ? 'active' as const : 'pending' as const;
    return { id: String(s.logical), label: s.label, state };
  });
  const currentStepIndex = Math.max(0, visibleSteps.findIndex(s => s.logical === step));
  const currentStepLabel = visibleSteps[currentStepIndex]?.label ?? '';
  const progressPercent = visibleSteps.length
    ? ((currentStepIndex + 1) / visibleSteps.length) * 100
    : 0;
  const isLastStep = step === INDIVIDUAL_STEP.DOCUMENTS;

  const navActions = (
    <>
      {step > INDIVIDUAL_STEP.PERSONAL ? (
        <Button type="button" variant="outline" onClick={handlePrevious}>
          {t('common.previous')}
        </Button>
      ) : (
        <span />
      )}
      <ButtonGroupRight>
        {!isLastStep ? (
          <Button type="button" onClick={handleNext} variant="primary">
            {t('register.continue')} →
          </Button>
        ) : (
          <Button
            type="submit"
            form="register-individual-form"
            data-action="register-complete"
            loading={loading}
            variant="gradient"
            ripple
          >
            {t('register.completeRegistration')}
          </Button>
        )}
      </ButtonGroupRight>
    </>
  );

  const visibleFieldErrors = hasAttemptedFinalSubmit
    ? fieldErrors
    : Object.fromEntries(
        Object.entries(fieldErrors).filter(([key]) => !CONSENT_ERROR_KEYS.includes(key))
      );
  const showErrorBanner =
    (!!error && (hasAttemptedFinalSubmit || step !== INDIVIDUAL_STEP.DOCUMENTS)) ||
    Object.keys(visibleFieldErrors).length > 0;

  const formBody = (
    <>
      {showErrorBanner && (
        <AlertBanner id="register-form-errors" $variant="error" role="alert">
          <ExclamationTriangleIcon aria-hidden />
          <span>{error || t('register.formErrorsBelow')}</span>
        </AlertBanner>
      )}

      <Form id="register-individual-form" onSubmit={handleSubmit} noValidate>
        {step === INDIVIDUAL_STEP.PERSONAL && (
          <FormSection title={t('register.individual.step1')} spacing="lg">
            <PersonalInfoSection
              formData={formData}
              setFormData={setFormData}
              t={t}
              showMembershipType={true}
              showTitleAndGender={true}
              showCriteriaBlock={true}
              lockCountryToGreece
              fieldErrors={fieldErrors}
              onFieldBlur={handleFieldBlur}
            />
          </FormSection>
        )}
        {step === INDIVIDUAL_STEP.ACCOUNT && !skipAccountStep && (
          <FormSection title={t('register.individual.step2')} spacing="lg">
            <FormField
              id="register-email"
              label={t('common.email')}
              type="email"
              name="email"
              autoComplete="email"
              value={formData.email}
              required
              error={fieldErrors.email}
              onChange={(e) => handleFieldChange('email', e.target.value)}
              onBlur={() => handleFieldBlur('email')}
            />
            <FormRow>
              <FormField
                id="register-password"
                label={t('register.password')}
                type={showPassword ? 'text' : 'password'}
                name="password"
                autoComplete="new-password"
                value={formData.password}
                required
                error={fieldErrors.password}
                helperText={t('register.passwordHint')}
                onChange={(e) => handleFieldChange('password', e.target.value)}
                onBlur={() => handleFieldBlur('password')}
                iconRight={
                  <PasswordVisibilityToggle
                    visible={showPassword}
                    onToggle={() => setShowPassword(v => !v)}
                    controlsId="register-password"
                  />
                }
              />
              <FormField
                id="register-confirm-password"
                label={t('register.confirmPassword')}
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                autoComplete="new-password"
                value={formData.confirmPassword}
                required
                error={fieldErrors.confirmPassword}
                onChange={(e) => handleFieldChange('confirmPassword', e.target.value)}
                onBlur={() => handleFieldBlur('confirmPassword')}
                iconRight={
                  <PasswordVisibilityToggle
                    visible={showConfirmPassword}
                    onToggle={() => setShowConfirmPassword(v => !v)}
                    controlsId="register-confirm-password"
                  />
                }
              />
            </FormRow>
          </FormSection>
        )}
        {step === INDIVIDUAL_STEP.CATEGORY && (
          <FormSection title={t('register.categoryQuestion')} spacing="lg">
            <ProfessionalActivitiesSection
              professionalActivities={formData.professionalActivities}
              setFormData={setFormData}
              t={t}
              onSelectionChange={() => setError(null)}
              transferFromIcom={formData.transferFromIcom}
              previousCardNumber={formData.previousCardNumber}
            />
          </FormSection>
        )}
        {step === INDIVIDUAL_STEP.WORK && showWorkStep && (
          <FormSection title={t('register.individual.workStep')} spacing="lg">
            <WorkDetailsSection
              formData={formData}
              setFormData={setFormData}
              t={t}
              fieldErrors={fieldErrors}
              showCheckboxes={true}
              onFieldBlur={handleFieldBlur}
            />
          </FormSection>
        )}
        {step === INDIVIDUAL_STEP.STUDIES && showStudiesStep && (
          <FormSection title={t('register.individual.studiesStep')} spacing="lg">
            <StudiesSection
              studies={formData.studies}
              onChange={(studies) => {
                setFormData(prev => ({ ...prev, studies }));
                setFieldErrors(prev => {
                  const next = { ...prev };
                  delete next.studies;
                  Object.keys(next).forEach(key => {
                    if (key.startsWith('studies.')) delete next[key];
                  });
                  return next;
                });
                setError(null);
              }}
              onEnterKey={handleNext}
              fieldErrors={fieldErrors}
            />
          </FormSection>
        )}
        {step === INDIVIDUAL_STEP.COMMITTEES && (
          <FormSection title={t('register.individual.step4')} spacing="lg">
            <SpacedBlock>
              <LinkButton type="button" onClick={() => setCommitteesModalOpen(true)}>
                {t('register.aboutCommittees')}
              </LinkButton>
            </SpacedBlock>
            {committeesLoadError && (
              <AlertBanner $variant="error" role="alert" style={{ marginBottom: 16 }}>
                <ExclamationTriangleIcon aria-hidden />
                <div>
                  <div>{t('register.committees.loadError')}</div>
                  <Button type="button" variant="outline" size="sm" onClick={() => loadCommittees()} style={{ marginTop: 8 }}>
                    {t('common.retry')}
                  </Button>
                </div>
              </AlertBanner>
            )}
            <MembershipSection
              formData={formData}
              setFormData={setFormData}
              committees={committees}
              t={t}
              showMembershipType={false}
              showHonoraryCheckbox={false}
              maxAdditionalCommittees={3}
              onCommitteeChange={() => {
                setError(null);
                setFieldErrors(prev => {
                  if (!prev.mainCommittee) return prev;
                  const next = { ...prev };
                  delete next.mainCommittee;
                  return next;
                });
              }}
            />
          </FormSection>
        )}
        {step === INDIVIDUAL_STEP.DOCUMENTS && (
          <FormSection title={t('register.individual.step5')} spacing="lg">
            <AlertBanner $variant="info">
              <InformationCircleIcon aria-hidden />
              <span>{t('register.documentsHint')}</span>
            </AlertBanner>
            <SpacedBlock>
              <LinkButton type="button" onClick={() => setGdprModalOpen(true)}>
                {t('register.gdprInfo')}
              </LinkButton>
            </SpacedBlock>
            <FileUpload
              onFileSelect={(file) => setFormData(prev => ({ ...prev, cv: file }))}
              onFileRemove={() => setFormData(prev => ({ ...prev, cv: null }))}
              currentFile={formData.cv}
              accept=".pdf,.doc,.docx"
              maxSize={5 * 1024 * 1024}
              label={t('document.type.cv')}
              hint={
                getEffectiveProfessionalPath(formData.professionalActivities) === ProfessionalPath.InterestedNonProfessional.toString()
                  ? t('register.individual.attachCv')
                  : t('register.documentsHint')
              }
            />
            <FileUpload
              onFileSelect={(file) => setFormData(prev => ({ ...prev, professionalActivityDocuments: file }))}
              onFileRemove={() => setFormData(prev => ({ ...prev, professionalActivityDocuments: null }))}
              currentFile={formData.professionalActivityDocuments}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              maxSize={5 * 1024 * 1024}
              label={
                getEffectiveProfessionalPath(formData.professionalActivities) === ProfessionalPath.ActiveMuseumProfessional.toString()
                  ? t('register.individual.proofOfEmployment')
                  : t('document.type.professionalActivity')
              }
              hint={
                getEffectiveProfessionalPath(formData.professionalActivities) === ProfessionalPath.ActiveMuseumProfessional.toString()
                  ? t('register.individual.proofOfEmployment')
                  : t('register.documentsHint')
              }
            />
            <FileUpload
              onFileSelect={(file) => setFormData(prev => ({ ...prev, studentID: file }))}
              onFileRemove={() => setFormData(prev => ({ ...prev, studentID: null }))}
              currentFile={formData.studentID}
              accept=".pdf,.jpg,.jpeg,.png"
              maxSize={5 * 1024 * 1024}
              label={
                getEffectiveProfessionalPath(formData.professionalActivities) === ProfessionalPath.Student.toString()
                  ? t('register.individual.proofOfEnrolment')
                  : t('document.type.studentID')
              }
              hint={
                getEffectiveProfessionalPath(formData.professionalActivities) === ProfessionalPath.Student.toString()
                  ? t('register.individual.proofOfEnrolment')
                  : t('register.documentsHint')
              }
            />
            {isResubmit && (
              <FormField
                id="register-applicant-note"
                label={t('applicationStatus.applicantNote')}
                textarea
                name="applicantNote"
                value={applicantNote}
                helperText={t('applicationStatus.applicantNotePlaceholder')}
                onChange={e => setApplicantNote(e.target.value)}
              />
            )}
            <SectionHeadingWithTop>{t('register.declarationHeading')}</SectionHeadingWithTop>
            <StatementBlock>{t('register.individual.individualDeclarationStatement')}</StatementBlock>
            <FormRow>
              <FormField
                label={t('register.declarationDateLabel')}
                type="date"
                value={formData.declarationDate}
                onChange={(e) => setFormData(prev => ({ ...prev, declarationDate: e.target.value }))}
              />
            </FormRow>
            <CheckboxContainer>
              <Checkbox
                type="checkbox"
                id="acceptedEthics"
                checked={formData.acceptedEthics}
                aria-invalid={(hasAttemptedFinalSubmit && !!fieldErrors.acceptedEthics) || undefined}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, acceptedEthics: e.target.checked }));
                  setFieldErrors(prev => {
                    if (!prev.acceptedEthics) return prev;
                    const next = { ...prev };
                    delete next.acceptedEthics;
                    return next;
                  });
                }}
              />
              <CheckboxLabel htmlFor="acceptedEthics">{t('register.individual.checkboxIndividualForm')}</CheckboxLabel>
            </CheckboxContainer>
            {hasAttemptedFinalSubmit && fieldErrors.acceptedEthics && (
              <Text size="sm" color="error" role="alert" style={{ marginBottom: 8 }}>
                {fieldErrors.acceptedEthics}
              </Text>
            )}
            <CheckboxContainer>
              <Checkbox
                type="checkbox"
                id="acceptedGdpr"
                checked={formData.acceptedGdpr}
                aria-invalid={(hasAttemptedFinalSubmit && !!fieldErrors.acceptedGdpr) || undefined}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, acceptedGdpr: e.target.checked }));
                  setFieldErrors(prev => {
                    if (!prev.acceptedGdpr) return prev;
                    const next = { ...prev };
                    delete next.acceptedGdpr;
                    return next;
                  });
                }}
              />
              <CheckboxLabel htmlFor="acceptedGdpr">{t('register.individual.checkboxIndividualGdpr')}</CheckboxLabel>
            </CheckboxContainer>
            {hasAttemptedFinalSubmit && fieldErrors.acceptedGdpr && (
              <Text size="sm" color="error" role="alert" style={{ marginBottom: 8 }}>
                {fieldErrors.acceptedGdpr}
              </Text>
            )}
          </FormSection>
        )}

      </Form>

      <Modal
        isOpen={committeesModalOpen}
        onClose={() => setCommitteesModalOpen(false)}
        title={t('register.aboutCommittees')}
        maxWidth="560px"
      >
        <TextWithMargin size="sm" color="secondary">
          {t('register.committeesInfoIntro')}
        </TextWithMargin>
        <ExternalLink href={t('register.committeesLink')} target="_blank" rel="noopener noreferrer">
          {t('register.committeesLink')}
        </ExternalLink>
        <CommitteesList>
          {COMMITTEES_INFO_LIST.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </CommitteesList>
      </Modal>

      <Modal
        isOpen={gdprModalOpen}
        onClose={() => setGdprModalOpen(false)}
        title={t('register.gdprInfo')}
        maxWidth="720px"
      >
        <GdprTabs role="tablist" aria-label={t('register.gdprInfo')}>
          <GdprTab
            type="button"
            role="tab"
            aria-selected={gdprVariant === 'with'}
            $active={gdprVariant === 'with'}
            onClick={() => setGdprVariant('with')}
          >
            {t('register.gdprWithNationalCommittee')}
          </GdprTab>
          <GdprTab
            type="button"
            role="tab"
            aria-selected={gdprVariant === 'without'}
            $active={gdprVariant === 'without'}
            onClick={() => setGdprVariant('without')}
          >
            {t('register.gdprWithoutNationalCommittee')}
          </GdprTab>
        </GdprTabs>
        <ScrollableStatementBlock role="tabpanel">
          {gdprVariant === 'with' ? t('register.gdprWithText') : t('register.gdprWithoutText')}
        </ScrollableStatementBlock>
      </Modal>
    </>
  );

  if (modeLoading) {
    return (
      <AuthWizardShell
        title={isResubmit ? t('register.resubmitApplication') : t('register.adminCreatingMember')}
        subtitle={t('common.loading')}
        titleId="register-individual-title"
      >
        <WizardCard variant="elevated">
          <Text size="sm" color="secondary">{t('common.loading')}</Text>
        </WizardCard>
      </AuthWizardShell>
    );
  }

  return (
    <AuthWizardShell
      title={
        isAdminCreate
          ? t('register.adminCreatingMember')
          : isResubmit
            ? t('register.resubmitApplication')
            : t('register.petitionNewMember')
      }
      subtitle={
        isAdminCreate
          ? t('register.adminCreatingMemberSubtitle')
          : isResubmit
            ? t('register.resubmitApplicationSubtitle')
            : t('register.individualSubtitle')
      }
      titleId="register-individual-title"
      backSlot={
        <BackLink to={isAdminCreate ? '/admin/members' : isResubmit ? `/application-status?reference=${encodeURIComponent(resubmitReference || referenceParam)}` : '/register'}>
          <ArrowLeftIcon aria-hidden />
          {t('common.back')}
        </BackLink>
      }
      footerSlot={navActions}
    >
      <WizardCard variant="elevated">
        <RegisterLayout>
          <StepperColumn aria-label={t('register.stepProgress', { current: currentStepIndex + 1, total: visibleSteps.length })}>
            <StepperWrapper>
              <Stepper
                steps={stepperSteps}
                orientation="vertical"
                showLabels
                responsive={false}
                ariaLabel={t('register.progressLabel')}
              />
            </StepperWrapper>
          </StepperColumn>
          <FormColumn>
            <WizardStepPanel stepKey={step} aria-label={currentStepLabel}>
              <MobileProgress>
                <ProgressMeta>
                  <Text size="sm" weight="medium" color="primary">
                    {t('register.stepProgress', { current: currentStepIndex + 1, total: visibleSteps.length })}
                  </Text>
                  <Text size="sm" color="secondary">{currentStepLabel}</Text>
                </ProgressMeta>
                <ProgressTrack
                  role="progressbar"
                  aria-valuenow={currentStepIndex + 1}
                  aria-valuemin={1}
                  aria-valuemax={visibleSteps.length}
                  aria-label={t('register.stepProgress', { current: currentStepIndex + 1, total: visibleSteps.length })}
                >
                  <ProgressFill $percent={progressPercent} />
                </ProgressTrack>
              </MobileProgress>
              {formBody}
            </WizardStepPanel>
          </FormColumn>
        </RegisterLayout>
      </WizardCard>
    </AuthWizardShell>
  );
};

export default RegisterIndividual;
